import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { collection, getDocs } from "firebase/firestore";
import * as XLSX from "xlsx";
import db from "./Firebase";

const parseDate = (val) => {
    if (!val) return null;
    if (typeof val?.toDate === "function") return val.toDate();
    if (val instanceof Date) return val;
    if (typeof val?.seconds === "number") return new Date(val.seconds * 1000);
    const d = new Date(val);
    return isNaN(d) ? null : d;
};

const fmtDate = (val) => {
    const d = parseDate(val);
    if (!d) return "-";
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const cv = (val, fb = "-") => (val === null || val === undefined || val === "" ? fb : String(val));
const compactPhone = (val) => String(val || "").replace(/\D/g, "").slice(-10);

export default function GlowUsersData() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [filterMember, setFilterMember] = useState("all");
    const [filterType, setFilterType] = useState("all");

    useEffect(() => {
        const fetchAll = async () => {
            setLoading(true);
            try {
                // Fetch all three collections in parallel
                const [pdSnap, shikshaSnap, funderSnap] = await Promise.all([
                    getDocs(collection(db, "personal_details")),
                    getDocs(collection(db, "nv_shiksha_subscriptions")),
                    getDocs(collection(db, "funder_users_list")),
                ]);

                // Build shiksha membership map: phone → {isMember, membershipType, endDate, startDate}
                const today = new Date();
                const shikshaMap = {};
                shikshaSnap.forEach(docSnap => {
                    const d = docSnap.data();
                    const phone = compactPhone(d.phone_no || docSnap.id);
                    if (!phone) return;
                    const end = parseDate(d.end_date);
                    // Keep the most recent / active record if multiple exist
                    const existing = shikshaMap[phone];
                    const isMember = end && end > today;
                    if (!existing || (isMember && !existing.isMember)) {
                        shikshaMap[phone] = {
                            isMember,
                            membershipType: cv(d.membership_type),
                            startDate: d.start_date || null,
                            endDate: d.end_date || null,
                        };
                    }
                });

                // Build funder set: U+ users
                const funderSet = new Set();
                funderSnap.forEach(d => {
                    const phone = compactPhone(d.data().phone_no || d.id);
                    if (phone) funderSet.add(phone);
                });

                // Build rows from personal_details (all users)
                const data = [];
                pdSnap.forEach(docSnap => {
                    const d = docSnap.data();
                    const phone = compactPhone(d.phone_no);
                    const shiksha = shikshaMap[phone] || null;
                    data.push({
                        id: docSnap.id,
                        name: cv(d.full_name),
                        phone,
                        username: cv(d.username),
                        createdDate: d.created_date || null,
                        isMember: shiksha?.isMember || false,
                        membershipType: shiksha?.membershipType || "-",
                        startDate: shiksha?.startDate || null,
                        endDate: shiksha?.endDate || null,
                        isUPlus: funderSet.has(phone),
                    });
                });

                data.sort((a, b) =>
                    (parseDate(b.createdDate)?.getTime() || 0) - (parseDate(a.createdDate)?.getTime() || 0)
                );
                setRows(data);
            } catch (err) {
                console.error("GlowUsersData fetch error:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchAll();
    }, []);

    const filtered = rows.filter(r => {
        const q = search.trim().toLowerCase();
        const matchSearch = !q ||
            r.phone.includes(q) ||
            r.name.toLowerCase().includes(q) ||
            r.username.toLowerCase().includes(q);
        const matchMember =
            filterMember === "all" ||
            (filterMember === "member" && r.isMember) ||
            (filterMember === "not_member" && !r.isMember);
        const matchType =
            filterType === "all" ||
            (filterType === "uplus" && r.isUPlus) ||
            (filterType === "normal" && !r.isUPlus);
        return matchSearch && matchMember && matchType;
    });

    const handleExport = () => {
        const sheetData = filtered.map((r, i) => ({
            "#": i + 1,
            "Name": r.name,
            "Phone No": r.phone,
            "Username": r.username,
            "Membership Type": r.membershipType,
            "Membership Start": fmtDate(r.startDate),
            "Membership End": fmtDate(r.endDate),
            "Member": r.isMember ? "Member" : "Not a Member",
            "User Type": r.isUPlus ? "U+" : "Normal User",
            "Joined Date": fmtDate(r.createdDate),
        }));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(sheetData), "All Users");
        XLSX.writeFile(wb, "glow_users_data.xlsx");
    };

    const memberCount = rows.filter(r => r.isMember).length;
    const uplusCount = rows.filter(r => r.isUPlus).length;

    return (
        <div className="container-fluid py-4">

            {/* HEADER */}
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                    <h3 className="mb-1">All Users Data</h3>
                    {/* <div className="text-muted" style={{ fontSize: "13px" }}>
                        personal_details · {rows.length} total &nbsp;·&nbsp;
                        <span style={{ color: "#16a34a", fontWeight: 600 }}>{memberCount} active members</span>
                        &nbsp;·&nbsp;
                        <span style={{ color: "#d97706", fontWeight: 600 }}>{uplusCount} U+</span>
                    </div> */}
                </div>
                <Link to="/" className="btn btn-outline-secondary btn-sm">Back</Link>
            </div>

            {/* FILTERS + SEARCH + EXPORT */}
            <div className="card mb-3">
                <div className="card-body py-3 px-4">
                    <div className="d-flex gap-3 align-items-center flex-wrap">
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Search by name, phone or username…"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            style={{ maxWidth: "280px" }}
                        />
                        <select
                            className="form-select"
                            value={filterMember}
                            onChange={e => setFilterMember(e.target.value)}
                            style={{ maxWidth: "170px" }}
                        >
                            <option value="all">All Members</option>
                            <option value="member">Member Only</option>
                            <option value="not_member">Not a Member</option>
                        </select>
                        <select
                            className="form-select"
                            value={filterType}
                            onChange={e => setFilterType(e.target.value)}
                            style={{ maxWidth: "160px" }}
                        >
                            <option value="all">All Users</option>
                            <option value="uplus">U+ Only</option>
                            <option value="normal">Normal Only</option>
                        </select>
                        {(search || filterMember !== "all" || filterType !== "all") && (
                            <span className="text-muted" style={{ fontSize: "13px" }}>
                                {filtered.length} result{filtered.length !== 1 ? "s" : ""}
                            </span>
                        )}
                        <button
                            className="btn btn-success ms-auto"
                            onClick={handleExport}
                            disabled={loading || filtered.length === 0}
                            style={{ minWidth: "140px", fontWeight: 700 }}
                        >
                            Export Excel
                        </button>
                    </div>
                </div>
            </div>

            {/* TABLE */}
            {loading ? (
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status" />
                    <div className="mt-2 text-muted" style={{ fontSize: "14px" }}>Loading…</div>
                </div>
            ) : (
                <div className="card">
                    <div className="table-responsive">
                        <table className="table table-bordered table-hover align-middle mb-0" style={{ fontSize: "13px" }}>
                            <thead className="table-dark">
                                <tr>
                                    <th>#</th>
                                    <th>Name</th>
                                    <th>Phone No</th>
                                    <th>Username</th>
                                    <th>Membership Type</th>
                                    <th>Start Date</th>
                                    <th>End Date</th>
                                    <th className="text-center">Member</th>
                                    <th className="text-center">User Type</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.length === 0 ? (
                                    <tr>
                                        <td colSpan="9" className="text-center py-4 text-muted">
                                            {search || filterMember !== "all" || filterType !== "all"
                                                ? "No results found"
                                                : "No data found"}
                                        </td>
                                    </tr>
                                ) : filtered.map((row, i) => (
                                    <tr key={row.id}>
                                        <td>{i + 1}</td>
                                        <td style={{ fontWeight: 600 }}>{row.name}</td>
                                        <td>{row.phone}</td>
                                        <td style={{ color: "#6b7280" }}>{row.username}</td>
                                        <td>{row.membershipType}</td>
                                        <td style={{ whiteSpace: "nowrap" }}>{fmtDate(row.startDate)}</td>
                                        <td style={{ whiteSpace: "nowrap" }}>{fmtDate(row.endDate)}</td>
                                        <td className="text-center">
                                            <span className={`badge ${row.isMember ? "bg-success" : "bg-secondary"}`}
                                                style={{ fontSize: "12px", padding: "5px 10px" }}>
                                                {row.isMember ? "✓ Member" : "Not a Member"}
                                            </span>
                                        </td>
                                        <td className="text-center">
                                            <span className={`badge ${row.isUPlus ? "bg-warning text-dark" : "bg-primary"}`}
                                                style={{ fontSize: "12px", padding: "5px 10px", fontWeight: 700 }}>
                                                {row.isUPlus ? "U+" : "Normal User"}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}
